/**
 * WEB-928 — механика иконки вкладки (favicon) публичных страниц.
 *
 * Дефолт приложения лежит в `frontend/public/favicon.svg` и подключён из
 * index.html. Воронка может показать свою: сюда приходит URL из
 * ПОЛЬЗОВАТЕЛЬСКОГО ввода, поэтому в DOM он попадает только через
 * sanitizeFaviconUrl.
 */
import { useEffect } from 'react';

/** Схемы, которым разрешено попасть в href иконки. */
const ALLOWED_PROTOCOLS = ['http:', 'https:'];

/**
 * Приводит значение из настроек к ссылке, которую безопасно положить в DOM.
 *
 * Пропускаем только абсолютные http(s). Всё остальное — null (иконки нет,
 * остаётся дефолт приложения):
 *  - `javascript:` / `vbscript:` и прочие исполняемые схемы;
 *  - `data:` — иконку в продукте задают ссылкой (загрузка в хранилище), а не
 *    байтами в настройках; разрешать data: значило бы тащить в настройки
 *    неограниченный blob и разбирать его MIME на доверие;
 *  - относительные пути — они указывали бы на наш же домен и ничего полезного
 *    пользователю не дают.
 */
export function sanitizeFaviconUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const url = value.trim();
  if (!url) return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (!ALLOWED_PROTOCOLS.includes(parsed.protocol)) return null;
  return url;
}

/**
 * Показывает переданную иконку на время жизни компонента.
 *
 * `null` = не трогать документ вовсе: у ресурса иконки нет, и дефолт из
 * index.html остаётся как был. При размонтировании/смене URL возвращаем
 * ровно тот href (и type), что стоял до нас — иначе иконка одной воронки
 * «залипала» бы на следующей.
 */
export function useDocumentFavicon(href: string | null): void {
  useEffect(() => {
    if (!href) return;
    const head = document.head;
    // rel~="icon" ловит и `icon`, и легаси `shortcut icon`.
    const existing = head.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    const link = existing ?? document.createElement('link');
    const created = existing === null;
    if (created) {
      link.setAttribute('rel', 'icon');
      head.appendChild(link);
    }
    const prevHref = link.getAttribute('href');
    const prevType = link.getAttribute('type');
    link.setAttribute('href', href);
    // Тип пользовательской иконки неизвестен (png/ico/svg), а унаследованный
    // от дефолта image/svg+xml соврал бы браузеру про формат.
    link.removeAttribute('type');
    return () => {
      if (created) {
        link.remove();
        return;
      }
      if (prevHref === null) link.removeAttribute('href');
      else link.setAttribute('href', prevHref);
      if (prevType === null) link.removeAttribute('type');
      else link.setAttribute('type', prevType);
    };
  }, [href]);
}
